export type ArtifactType =
  | "text/markdown"
  | "application/code"
  | "text/html"
  | "image/svg+xml"
  | "application/vnd.mermaid";

export type ArtifactStatus = "streaming" | "complete" | "error";

/**
 * Single field for “assistant is producing a document artifact” UX
 * (composer lock, chat strip, panel closed until ready).
 * Extend with new literals only when behavior branches need a distinct policy.
 */
export type ArtifactUiPhase = "idle" | "document_generating";

export function isDocumentArtifactGenerating(
  phase: ArtifactUiPhase,
): phase is "document_generating" {
  return phase === "document_generating";
}

export interface Artifact {
  id: string;
  identifier: string;
  title: string;
  type: ArtifactType;
  language: string | null;
  content: string;
  version: number;
  status: ArtifactStatus;
  error?: string;
  chatId?: string;
  messageId?: string;
  createdAt?: string;
}

export interface ArtifactStartEvent {
  id: string;
  identifier: string;
  title: string;
  type: ArtifactType;
  language: string | null;
}

export interface ArtifactDeltaEvent {
  id: string;
  contentDelta: string;
}

export interface ArtifactEndEvent {
  id: string;
  identifier: string;
  version: number;
}

export interface ArtifactErrorEvent {
  id: string;
  error: string;
}

export interface ArtifactListItem {
  id: string;
  chatId: string;
  messageId: string;
  identifier: string;
  title: string;
  type: ArtifactType;
  language: string | null;
  version: number;
  createdAt: string;
}

export interface ArtifactListResponse {
  items: ArtifactListItem[];
  totalCount: number;
}

export interface ArtifactFull extends ArtifactListItem {
  content: string;
}

export const ARTIFACT_TYPE_LABELS: Record<ArtifactType, string> = {
  "text/markdown": "Markdown",
  "application/code": "Code",
  "text/html": "HTML",
  "image/svg+xml": "SVG",
  "application/vnd.mermaid": "Mermaid",
};

export const ARTIFACT_TYPE_EXTENSIONS: Record<ArtifactType, string> = {
  "text/markdown": ".md",
  "application/code": ".txt",
  "text/html": ".html",
  "image/svg+xml": ".svg",
  "application/vnd.mermaid": ".mmd",
};
